import { useHistory } from 'react-router-dom'

import { useAccountRoutes } from 'containers/routes'
import { DASHBOARD_KIND, WIDGET_KIND } from 'lib/dashboards-constants'
import { getMetricNamespace, getWidgetLayout } from 'lib/dashboards-helpers'
import {
  useDeleteDashboard,
  useUpdateDashboardQuery,
  useCreateWidgetQuery,
  useDeleteWidgetQuery,
  useUpdateWidgetQuery
  , useDuplicateDashboardQuery
} from 'hooks/api'

const formatStatistic = (stat) => {
  if (Array.isArray(stat)) return stat
  if (stat === 'avg') return ['avg', 'min', 'max']
  else return [stat]
}
// TODO: extract somewhere
export const formatWidgetPayload = ({ widgets, creating, kind, data }) => {
  let definition = {}

  switch (kind) {
    case WIDGET_KIND.METRICS:
      definition = {
        metrics: data.payloads
          .filter(payload => !!payload)
          .map(payload => ({
            resourceType: payload?.resourceType,
            namespace: getMetricNamespace(kind, payload?.resourceType, payload?.metric),
            ...(payload.targets && { resource: payload.targets[0] }),
            ...(payload.stats && { stats: formatStatistic(payload.stats) }),
            ...(payload.metric && { metric: payload.metric })
          }))
      }
      break

    case WIDGET_KIND.INVOCATIONS:
      definition = {
        service: 'lambda',
        targets: data.targets,
        ...(data.flag && { flag: data.flag })
      }
      break

    case WIDGET_KIND.EVENTS:
      definition = {
        type: data.type?.length ? data.type : ['error', 'insight', 'alarm'],
        ...(data?.targets?.length && { targets: data.targets })
      }
      break

    case WIDGET_KIND.TEXT:
      definition = {
        text: data?.text,
        size: data?.size
      }
      break
    case WIDGET_KIND.LOGS:
      definition = {
        targets: data?.targets,
        query: data?.query,
        service: data?.service
      }
      break
  }

  const WIDGET_SIZE = data?.layout ? data?.layout : kind === WIDGET_KIND.TEXT ? { height: 1, width: 8 } : undefined

  return {
    ...(creating && { kind }),
    ...(creating && { layout: getWidgetLayout(widgets, WIDGET_SIZE) }),
    ...(data?.name && { name: `${data.name} copy` }),
    definition
  }
}

const useMutations = ({ dashboard, widgets }) => {
  const history = useHistory()
  const routes = useAccountRoutes()

  const { mutate: deleteDashboard } = useDeleteDashboard()
  const { mutate: duplicateDashboard, isLoading: duplicating } = useDuplicateDashboardQuery()
  const { mutate: updateDashboard } = useUpdateDashboardQuery()
  const { mutate: createWidget, mutateAsync: createWidgetAsync } = useCreateWidgetQuery()
  const { mutate: deleteWidget, isLoading: deleting } = useDeleteWidgetQuery()
  const { mutate: updateWidget } = useUpdateWidgetQuery()

  const handlers = {
    duplicate: (dashboardId) => {
      duplicateDashboard(dashboardId)
    },
    duplicating,
    remove: (dashboardId) => {
      deleteDashboard(dashboardId)
      history.push(routes.dashboards.url())
    },
    deleting,

    update: {
      title: (value) => {
        updateDashboard({ name: value })
      },

      layout: (layout) => {
        const changedWidgets = layout.filter((widget) => {
          const oldWidget = widgets.find(x => x.id === widget.i)

          if (!oldWidget) return false
          if (oldWidget.kind === WIDGET_KIND.PLACEHOLDER) return false
          if (widget.x !== oldWidget.layout.x || widget.y !== oldWidget.layout.y) return true
          if (widget.w !== oldWidget.layout.width || widget.h !== oldWidget.layout.height) return true

          return false
        })

        changedWidgets.forEach(widget => {
          const payload = {
            layout: {
              x: widget.x,
              y: widget.y,
              width: widget.w,
              height: widget.h
            }
          }

          updateWidget({ widgetId: widget.i, payload })
        })
      },

      timespan: (value) => {
        updateDashboard({ defaultRelativeTimespan: parseInt(value) })
      }
    },

    widgets: {
      add: (kind, data) => {
        const payload = formatWidgetPayload({ widgets, creating: true, kind, data })
        createWidget(payload)
      },

      delete: (widget) => {
        deleteWidget(widget.id)
      },

      // This handler opens an edit drawer
      // This handler is called when user clicks Edit button in widget dropdown menu
      edit: (widget) => {
        if (dashboard?.kind === DASHBOARD_KIND.RESOURCE_GROUP) return
        // console.log(widget)
        // const pathname = dashboard?.kind === DASHBOARD_KIND.RESOURCE_GROUP
        //   ? routes.dashboards.resourceGroups.widget.url({ groupId: widget?.definition?.resourceGroup, widgetId: widget?.definition?.metrics?.[0].metric })
        //   : routes.dashboards.widget.url({ dashboardId: dashboard.id, widgetId: widget.id })
        const pathname = routes.dashboards.widget.url({ dashboardId: dashboard.id, widgetId: widget.id })
        history.push({ pathname })
      },

      update: (widget, data) => {
        const payload = formatWidgetPayload({ creating: false, kind: widget.kind, data })
        updateWidget({ widgetId: widget.id, payload })
      },

      updateName: (widget, name) => updateWidget({ widgetId: widget.id, payload: { name } }),

      duplicate: async (widget) => {
        const formatDuplicateDefinition = (widget) => {
          switch (widget.kind) {
            case WIDGET_KIND.METRICS:
              return {
                payloads: widget?.definition?.metrics?.map(metric => ({
                  resourceType: metric.resourceType,
                  metric: metric.metric,
                  targets: [metric.resource],
                  stats: metric.stats
                }))
              }

            case WIDGET_KIND.INVOCATIONS:
              return widget.definition
            case WIDGET_KIND.EVENTS:
              return widget.definition
            default: return {}
          }
        }

        const data = {
          ...formatDuplicateDefinition(widget),
          ...(widget?.name && { name: widget.name }),
          layout: widget.layout
        }

        const payload = formatWidgetPayload({ widgets, creating: true, kind: widget.kind, data })

        try {
          const data = await createWidgetAsync(payload)
          handlers.widgets.edit(data)
        } catch { }
      }
    }
  }

  return handlers
}

export default useMutations
